import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import OrderModal from '../components/Orders/OrderModal';

const Orders = ({ user }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('view'); // 'view' или 'edit'

    // Состояние для пагинации
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        totalCount: 0,
        totalPages: 1
    });

    // Состояние для фильтров
    const [filters, setFilters] = useState({
        caseNumber: '',
        personName: '',
        dateAcceptance: '',
        collectionPeriodFrom: '',
        collectionPeriodTo: '',
        institutionCollection: ''
    });

    const [message, setMessage] = useState('');

    // Мемоизированная функция загрузки данных
    const loadOrders = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                pageSize: pagination.pageSize.toString(),
                ...filters
            });

            const response = await api.getOrders(params);
            if (response.data) {
                setOrders(response.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    totalCount: response.data.totalCount || 0,
                    totalPages: response.data.totalPages || 1
                }));
            }
        } catch (error) {
            setMessage('Ошибка загрузки приказов');
            console.error('Load orders error:', error);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.pageSize, filters]);

    // Загрузка данных при изменении страницы или фильтров
    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    // Обработчик двойного клика по строке таблицы
    const handleRowDoubleClick = async (order) => {
        try {
            setLoading(true);
            // Загружаем полные данные приказа
            const orderData = await api.getOrderById(order.id);
            setSelectedOrder(orderData.data);
            setModalMode('view');
            setShowModal(true);
        } catch (error) {
            setMessage('Ошибка загрузки данных приказа');
            console.error('Error loading order details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Обработчик создания нового приказа
    const handleCreateOrder = () => {
        setSelectedOrder(null);
        setModalMode('edit');
        setShowModal(true);
    };

    // Обработчик редактирования приказа
    const handleEditOrder = (order) => {
        setSelectedOrder(order);
        setModalMode('edit');
        setShowModal(true);
    };

    // Обработчик изменения фильтров с сбросом на первую страницу
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Очистка всех фильтров
    const clearFilters = () => {
        setFilters({
            caseNumber: '',
            personName: '',
            dateAcceptance: '',
            collectionPeriodFrom: '',
            collectionPeriodTo: '',
            institutionCollection: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Навигация по страницам
    const goToPage = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    // Обработчик удаления приказа
    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот приказ?')) {
            return;
        }

        try {
            await api.deleteOrder(id);
            setMessage('Приказ удален успешно');
            loadOrders();
        } catch (error) {
            setMessage('Ошибка удаления приказа');
            console.error('Delete order error:', error);
        }
    };

    // Функции форматирования
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(value));
    };

    const formatDateToRussian = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    // Генерация номеров страниц для пагинации
    const generatePageNumbers = () => {
        const pages = [];
        const current = pagination.page;
        const total = pagination.totalPages;

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (current > 3) pages.push('...');
            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }

        return pages;
    };

    return (
        <div className="page">
            <h2>Приказы</h2>

            {/* Панель управления */}
            <div className="page-header">
                {user && (user.role === 'admin' || user.role === 'lawyer') && (
                    <button
                        onClick={handleCreateOrder}
                        className="btn-primary"
                    >
                        Добавить приказ
                    </button>
                )}

                <button
                    onClick={loadOrders}
                    className="btn-secondary"
                    disabled={loading}
                >
                    {loading ? 'Обновление...' : 'Обновить список'}
                </button>
            </div>

            {message && (
                <div className={`message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}

            {/* Панель фильтров */}
            <div className="filters-panel">
                <div className="filters-header">
                    <h4>Фильтры</h4>
                    <button
                        onClick={clearFilters}
                        className="btn-secondary btn-clear-filters"
                    >
                        Очистить фильтры
                    </button>
                </div>

                <div className="filters-grid">
                    <div className="filter-group caseNumber">
                        <label>Номер приказа</label>
                        <input
                            type="text"
                            value={filters.caseNumber}
                            onChange={(e) => handleFilterChange('caseNumber', e.target.value)}
                            placeholder="По номеру приказа"
                        />
                    </div>

                    <div className="filter-group personName">
                        <label>ФИО</label>
                        <input
                            type="text"
                            value={filters.personName}
                            onChange={(e) => handleFilterChange('personName', e.target.value)}
                            placeholder="По ФИО"
                        />
                    </div>

                    <div className="filter-group dateAcceptance">
                        <label>Дата принятия заявления</label>
                        <input
                            type="date"
                            value={filters.dateAcceptance}
                            onChange={(e) => handleFilterChange('dateAcceptance', e.target.value)}
                        />
                    </div>

                    <div className="filter-group collectionPeriodFrom">
                        <label>Период взыскания с</label>
                        <input
                            type="date"
                            value={filters.collectionPeriodFrom}
                            onChange={(e) => handleFilterChange('collectionPeriodFrom', e.target.value)}
                        />
                    </div>

                    <div className="filter-group collectionPeriodTo">
                        <label>Период взыскания по</label>
                        <input
                            type="date"
                            value={filters.collectionPeriodTo}
                            onChange={(e) => handleFilterChange('collectionPeriodTo', e.target.value)}
                        />
                    </div>

                    <div className="filter-group institutionCollection">
                        <label>Учреждение для взыскания</label>
                        <input
                            type="text"
                            value={filters.institutionCollection}
                            onChange={(e) => handleFilterChange('institutionCollection', e.target.value)}
                            placeholder="По учреждению"
                        />
                    </div>
                </div>

                <div className="filter-stats">
                    Всего записей: {pagination.totalCount}
                    {Object.values(filters).some(filter => filter !== '') && (
                        <span className="filters-applied">(применены фильтры)</span>
                    )}
                </div>
            </div>

            {/* Пагинация сверху */}
            <div className="pagination-top">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages}
                    ({pagination.totalCount} всего записей)
                </div>

                <div className="pagination-controls">
                    <button
                        onClick={() => goToPage(1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-first"
                    >
                        ⟪
                    </button>
                    <button
                        onClick={() => goToPage(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-prev"
                    >
                        ⟨
                    </button>

                    {generatePageNumbers().map((pageNum, index) => (
                        pageNum === '...' ? (
                            <span key={index} className="pagination-ellipsis">...</span>
                        ) : (
                            <button
                                key={index}
                                onClick={() => goToPage(pageNum)}
                                className={`btn-pagination ${pagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                            >
                                {pageNum}
                            </button>
                        )
                    ))}

                    <button
                        onClick={() => goToPage(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-next"
                    >
                        ⟩
                    </button>
                    <button
                        onClick={() => goToPage(pagination.totalPages)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-last"
                    >
                        ⟫
                    </button>
                </div>
            </div>

            {/* Таблица приказов */}
            <div className="orders-table-container">
                <h3>Список приказов</h3>

                {loading && orders.length === 0 ? (
                    <div className="loading">Загрузка...</div>
                ) : orders.length === 0 ? (
                    <div className="no-data">
                        {Object.values(filters).some(filter => filter !== '')
                            ? 'Нет записей, соответствующих фильтрам'
                            : 'Нет данных о приказах'
                        }
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table className="orders-table">
                            <thead>
                                <tr style={{ height: '50px', minHeight: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    <th rowSpan="2" style={{ width: '30px', minWidth: '30px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        № приказа
                                    </th>
                                    <th rowSpan="2" style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        <div>Дата принятия</div>
                                        <div>заявления судом</div>
                                    </th>
                                    <th rowSpan="2" style={{ width: '200px', minWidth: '200px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        ФИО
                                    </th>
                                    <th colSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        Период взыскания
                                    </th>

                                    <th colSpan="3" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        Взысканная судом сумма
                                    </th>
                                    <th rowSpan="2" style={{ width: '60px', minWidth: '60px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        Остаток
                                    </th>
                                </tr>
                                <tr style={{ height: '50px', minHeight: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                    <th style={{ width: '80px', minWidth: '80px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        С
                                    </th>
                                    <th style={{ width: '80px', minWidth: '80px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        По
                                    </th>
                                    <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        Взносы
                                    </th>
                                    <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        Пени
                                    </th>
                                    <th style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        Пошлина
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => (
                                    <tr
                                        key={order.id}
                                        onDoubleClick={() => handleRowDoubleClick(order)}
                                        style={{ cursor: 'pointer' }}
                                        className="order-row"
                                    >
                                        <td className="order-case-number">
                                            {order.caseNumber}
                                        </td>
                                        <td className="order-date-acceptance">
                                            {formatDateToRussian(order.dateAcceptance)}
                                        </td>
                                        <td className="order-person">
                                            {order.person?.name || '-'}
                                        </td>
                                        <td className="order-period-from">
                                            {formatDateToRussian(order.collectionPeriodFrom)}
                                        </td>
                                        <td className="order-period-to">
                                            {formatDateToRussian(order.collectionPeriodTo)}
                                        </td>
                                        <td className="order-amount-fee number-cell">
                                            {formatCurrency(order.amountRecoveredFee)}
                                        </td>
                                        <td className="order-amount-penalties number-cell">
                                            {formatCurrency(order.amountRecoveredPenalties)}
                                        </td>
                                        <td className="order-amount-toll number-cell">
                                            {formatCurrency(order.amountRecoveredToll)}
                                        </td>
                                        <td className="order-amount-fee number-cell">
                                            {formatCurrency(order.balanceFee + order.balancePenalties + order.balanceToll)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Пагинация снизу */}
            <div className="pagination-bottom">
                <div className="pagination-info">
                    Страница {pagination.page} из {pagination.totalPages}
                    ({pagination.totalCount} всего записей)
                </div>

                <div className="pagination-controls">
                    <button
                        onClick={() => goToPage(1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-first"
                    >
                        ⟪
                    </button>
                    <button
                        onClick={() => goToPage(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="btn-pagination btn-pagination-prev"
                    >
                        ⟨
                    </button>

                    {generatePageNumbers().map((pageNum, index) => (
                        pageNum === '...' ? (
                            <span key={index} className="pagination-ellipsis">...</span>
                        ) : (
                            <button
                                key={index}
                                onClick={() => goToPage(pageNum)}
                                className={`btn-pagination ${pagination.page === pageNum ? 'btn-pagination-active' : 'btn-pagination-inactive'}`}
                            >
                                {pageNum}
                            </button>
                        )
                    ))}

                    <button
                        onClick={() => goToPage(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-next"
                    >
                        ⟩
                    </button>
                    <button
                        onClick={() => goToPage(pagination.totalPages)}
                        disabled={pagination.page === pagination.totalPages}
                        className="btn-pagination btn-pagination-last"
                    >
                        ⟫
                    </button>
                </div>
            </div>

            {/* Модальное окно с деталями приказа */}
            {showModal && (
                <OrderModal
                    order={selectedOrder}
                    onClose={() => {
                        setShowModal(false);
                        setSelectedOrder(null);
                    }}
                    onUpdate={loadOrders}
                    isEditMode={modalMode === 'edit'}
                    accountNumber={selectedOrder?.accountNumber}
                    onBackToAccount={() => {
                        setShowModal(false);
                        setSelectedOrder(null);
                    }}
                    user={user}
                />
            )}
        </div>
    );
};

export default Orders;